import {
  ShieldCheck, Users, IdentificationCard, Eye, House, UserCircle, Briefcase,
  Wrench, Receipt, Buildings,
} from "@phosphor-icons/react";

/**
 * Rollen & Berechtigungen – Dokumentationsseite für Admins und Endnutzer.
 *
 * Erklärt die 9 verfügbaren Rollen mit Anwendungsfall und typischen Rechten.
 * Reine Read-Only-Seite (keine Backend-Calls).
 */

const ROLES = [
  {
    key: "platform_admin",
    label: "Plattform-Administrator",
    scope: "System (mandantenübergreifend)",
    icon: ShieldCheck,
    tone: "rose",
    description: "Höchste Ebene. Verwaltet alle Mandanten (Kunden), legt neue Mandanten an, sperrt, vergibt Kundennummern, schaltet Module pro Mandant frei.",
    can: [
      "Alle Mandanten sehen und bearbeiten",
      "Mandanten anlegen / sperren",
      "Nutzer pro Mandant CRUD",
      "Module pro Mandant aktivieren / deaktivieren",
      "Plattform-Settings (Branding, Domains)",
    ],
    cant: ["Operativ in einem Mandanten arbeiten (z. B. Mietverhältnis anlegen)"],
  },
  {
    key: "platform_support",
    label: "Plattform · Support",
    scope: "System (intern)",
    icon: Wrench,
    tone: "blue",
    description: "Hilft Mandanten bei Anfragen. Lesezugriff auf Mandanten, kann Plattform-Tickets bearbeiten.",
    can: ["Mandanten LESEN", "Nutzer LESEN", "Tickets bearbeiten"],
    cant: ["Mandanten anlegen / editieren", "Abrechnung ändern", "Module togglen"],
  },
  {
    key: "platform_billing",
    label: "Plattform · Buchhaltung",
    scope: "System (intern)",
    icon: Receipt,
    tone: "amber",
    description: "Verwaltet Abos, Rechnungen, Pakete. Kein Zugriff auf Nutzerrechte/Settings.",
    can: ["Rechnungen / Abos", "Mandanten LESEN"],
    cant: ["Mandanten editieren", "Nutzer verwalten", "Modul-Toggles"],
  },
  {
    key: "platform_tech",
    label: "Plattform · Technik",
    scope: "System (intern)",
    icon: Wrench,
    tone: "indigo",
    description: "Verantwortlich für System-/Modul-Konfiguration. Kann Mandanten editieren und Module togglen.",
    can: ["Mandanten editieren", "Module togglen", "Plattform-Settings"],
    cant: ["Mandanten anlegen", "Rechnungen", "Audit-Logs"],
  },
  {
    key: "platform_sales",
    label: "Plattform · Vertrieb",
    scope: "System (intern)",
    icon: Briefcase,
    tone: "emerald",
    description: "Onboarding neuer Kunden. Darf Mandanten anlegen, NICHT editieren.",
    can: ["Mandanten ANLEGEN", "Mandanten LESEN"],
    cant: ["Mandanten editieren", "Rechnungen", "Settings"],
  },
  {
    key: "platform_compliance",
    label: "Plattform · Compliance / Datenschutz",
    scope: "System (intern)",
    icon: ShieldCheck,
    tone: "slate",
    description: "Audit-Logs, Löschanträge, Datenschutz. Read-only sonst.",
    can: ["Audit-Logs lesen", "Datenschutz-Aktionen", "Mandanten LESEN"],
    cant: ["Operative Änderungen", "Module togglen"],
  },
  {
    key: "super_admin",
    label: "Super-Administrator (Mandant)",
    scope: "Mandant",
    icon: ShieldCheck,
    tone: "indigo",
    description: "Höchste Rolle innerhalb eines Mandanten (Hausverwaltung). Verwaltet alle Stammdaten, Nutzer und Settings dieses Mandanten.",
    can: [
      "Unternehmensstammdaten pflegen",
      "Nutzer im eigenen Mandant anlegen / deaktivieren",
      "Alle Objekte / Mietverhältnisse / Dokumente verwalten",
      "Briefkopf, Bank, NK-Schemas konfigurieren",
    ],
    cant: ["Andere Mandanten sehen", "Module aktivieren (nur platform_admin)"],
  },
  {
    key: "admin",
    label: "Administrator",
    scope: "Mandant",
    icon: IdentificationCard,
    tone: "indigo",
    description: "Administrative Rechte ohne Zugriff auf kritische Tenant-Einstellungen. Typisch für die IT-/Compliance-Verantwortliche:n.",
    can: ["Nutzer verwalten", "Settings ändern", "Mandanten-übergreifende Reports"],
    cant: ["Mandant löschen", "Modul-Zugriff verändern"],
  },
  {
    key: "management",
    label: "Geschäftsführung",
    scope: "Mandant",
    icon: Briefcase,
    tone: "slate",
    description: "Strategischer Überblick. Sieht alle Objekte/Konten/Reports, kann unterschreiben (z. B. Eigentümerreports), greift selten ins Tagesgeschäft ein.",
    can: ["Alles lesen", "Eigentümerreports freigeben", "Briefe unterschreiben"],
    cant: ["Nutzer anlegen"],
  },
  {
    key: "manager",
    label: "Verwalter:in",
    scope: "Mandant",
    icon: House,
    tone: "emerald",
    description: "Hauptnutzer der Software. Pflegt Objekte, Mietverhältnisse, Schreiben, Dokumente, Tickets.",
    can: [
      "Objekte/Einheiten/Mietverhältnisse CRUD",
      "Schreiben & Dokumente erstellen",
      "Aufgaben & Wiedervorlagen",
      "Tickets / Schadensmanagement",
    ],
    cant: ["Stammdaten der Firma ändern", "Nutzerverwaltung"],
  },
  {
    key: "accountant",
    label: "Buchhaltung",
    scope: "Mandant",
    icon: Receipt,
    tone: "amber",
    description: "Spezialisiert auf Mietkonten, Sollstellungen, Zahlungseingänge, Mahnwesen, NK-Abrechnung, DATEV-Export.",
    can: ["Mietkonto-Buchungen", "Sollstellungen generieren", "Mahnstufen", "NK-Abrechnungen"],
    cant: ["Stammdaten ändern", "Nutzerverwaltung"],
  },
  {
    key: "vermietung",
    label: "Vermietung",
    scope: "Mandant",
    icon: House,
    tone: "teal",
    description: "Phase 21z. Spezialisiert auf Vermarktung leerer Einheiten, Bewerbermanagement, Selbstauskünfte.",
    can: ["Bewerbungen verwalten", "Exposés erstellen", "Verträge vorbereiten"],
    cant: ["Mietkonto-Buchungen", "Nutzerverwaltung"],
  },
  {
    key: "support",
    label: "Support",
    scope: "Mandant",
    icon: Wrench,
    tone: "blue",
    description: "Bearbeitet Mieter-/Eigentümer-Anliegen, Tickets, schreibt Korrespondenz. Kein Zugriff auf Buchhaltung.",
    can: ["Tickets", "Schriftverkehr", "Termine"],
    cant: ["Mietkonto-Änderungen", "Vertragsanlage"],
  },
  {
    key: "external_service",
    label: "Externer Dienstleister",
    scope: "Mandant (eingeschränkt)",
    icon: Wrench,
    tone: "slate",
    description: "Phase 21z. Externe Handwerker / Dienstleister mit objektbezogenem Zugang. Sieht nur freigegebene Objekte.",
    can: ["Tickets bearbeiten (zugeteilt)", "Aufträge bestätigen"],
    cant: ["Vertragsdaten", "Mietkonten", "Stammdaten"],
  },
  {
    key: "viewer",
    label: "Nur-Lesen",
    scope: "Mandant",
    icon: Eye,
    tone: "slate",
    description: "Reiner Read-Only-Zugriff. Typisch für externe Berater, Wirtschaftsprüfer.",
    can: ["Alles lesen"],
    cant: ["Nichts ändern"],
  },
  {
    key: "owner",
    label: "Eigentümer (Portal)",
    scope: "Eigentümer-Portal",
    icon: UserCircle,
    tone: "navy",
    description: "Externer Eigentümer mit Zugriff auf das Eigentümer-Portal. Sieht nur die eigenen Objekte/Belege. Kann zu mehreren Mandanten gehören (Phase 21x).",
    can: ["Eigene Objekte ansehen", "Kosten & Abschläge prüfen", "Dokumente herunterladen", "Mandanten-Switcher nutzen"],
    cant: ["Andere Eigentümer sehen", "Operative Daten ändern"],
  },
  {
    key: "tenant",
    label: "Mieter:in (Portal)",
    scope: "Mieter-Portal",
    icon: Users,
    tone: "teal",
    description: "Mieter mit Zugriff auf das Mieter-Portal (Self-Service).",
    can: ["Eigenes Mietkonto ansehen", "Schadensmeldungen einreichen", "Kontaktdaten ändern"],
    cant: ["Andere Mietverhältnisse sehen"],
  },
];

const TONE_BG = {
  rose: "bg-rose-50 text-rose-700 border-rose-200",
  indigo: "bg-indigo-50 text-indigo-700 border-indigo-200",
  slate: "bg-slate-100 text-slate-700 border-slate-200",
  emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  navy: "bg-slate-900 text-white border-slate-900",
  teal: "bg-teal-50 text-teal-700 border-teal-200",
};

export default function Rollen() {
  return (
    <div className="space-y-5 max-w-6xl" data-testid="rollen-page">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
          <ShieldCheck size={24} className="text-slate-700" />
        </div>
        <div>
          <h1 className="text-2xl sm:text-3xl font-semibold tracking-tight">Rollen & Berechtigungen</h1>
          <p className="text-sm text-slate-500 mt-0.5">
            Übersicht aller verfügbaren Rollen in Domivus inkl. typischer Anwendungsfälle und Rechte.
            Die Zuordnung erfolgt je Nutzer und Mandant.
          </p>
        </div>
      </div>

      {/* Plattform-Struktur Diagramm */}
      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-6" data-testid="role-hierarchy">
        <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-300 mb-3">
          Plattform-Struktur · 4 Ebenen
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {[
            { lvl: "1", title: "Plattform", subtitle: "Systembetreiber", roles: ["Plattform-Admin", "Support", "Buchhaltung", "Technik", "Vertrieb"], color: "bg-rose-500/20 border-rose-400/30" },
            { lvl: "2", title: "Mandant", subtitle: "Hausverwaltung (Kunde)", roles: ["Super-Admin", "Admin", "Manager", "Buchhaltung", "Support"], color: "bg-indigo-500/20 border-indigo-400/30" },
            { lvl: "3", title: "Eigentümer", subtitle: "Portal-Nutzer", roles: ["Owner (kann zu mehreren Mandanten gehören)"], color: "bg-emerald-500/20 border-emerald-400/30" },
            { lvl: "4", title: "Mieter & Dienstleister", subtitle: "Self-Service", roles: ["Tenant", "Externe Dienstleister"], color: "bg-teal-500/20 border-teal-400/30" },
          ].map((lvl) => (
            <div key={lvl.lvl} className={`border rounded-xl p-3 ${lvl.color}`}>
              <div className="text-[10px] font-mono text-slate-300 uppercase tracking-wider">Ebene {lvl.lvl}</div>
              <div className="text-base font-semibold mt-0.5">{lvl.title}</div>
              <div className="text-[11px] text-slate-300 mb-2">{lvl.subtitle}</div>
              <ul className="text-[11px] space-y-0.5 text-slate-100">
                {lvl.roles.map((r, i) => <li key={i}>· {r}</li>)}
              </ul>
            </div>
          ))}
        </div>
        <div className="text-[11px] text-slate-300 mt-3 leading-relaxed">
          <strong className="text-slate-100">Strikte Datenisolation:</strong> Mandanten sehen niemals Daten anderer Mandanten.
          Eigentümer mit Memberships in mehreren Mandanten wechseln über den Switcher im Portal-Header.
          Plattform-Admins arbeiten mandantenübergreifend.
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {ROLES.map((r) => (
          <div key={r.key} className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm" data-testid={`role-card-${r.key}`}>
            <div className="flex items-start gap-3 mb-3">
              <div className={`w-10 h-10 rounded-xl border flex items-center justify-center ${TONE_BG[r.tone]}`}>
                <r.icon size={18} weight="duotone" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 font-mono">{r.key}</div>
                <div className="text-lg font-semibold text-slate-900 leading-tight">{r.label}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{r.scope}</div>
              </div>
            </div>
            <p className="text-sm text-slate-700 leading-relaxed mb-3">{r.description}</p>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <div className="font-semibold text-emerald-700 mb-1">Darf</div>
                <ul className="space-y-1 text-slate-700">
                  {r.can.map((c, i) => <li key={i}>✓ {c}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-semibold text-rose-700 mb-1">Darf nicht</div>
                <ul className="space-y-1 text-slate-700">
                  {r.cant.map((c, i) => <li key={i}>✗ {c}</li>)}
                </ul>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-xs text-slate-600 mt-4">
        <strong>Hinweis:</strong> Mit dem Modul „Erweiterte Rollen & Rechte" lassen sich zusätzlich
        granulare Berechtigungen pro Funktion vergeben (z. B. „darf NK-Abrechnungen freigeben, aber
        keine Mahnungen senden"). Dieses Modul ist standardmäßig deaktiviert und kann je Mandant
        vom Plattform-Admin aktiviert werden.
      </div>
    </div>
  );
}
